import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { getAccounts, getTransactions, submitDeposit, submitWithdrawal, uploadTransactionProof } from '@/utils/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { CreditCard, Bank, CurrencyBtc, ArrowRight, ArrowUpRight, ArrowDownLeft, X } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { formatMoney, formatMetric } from '@/utils/currency';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CRYPTOS_FALLBACK = [
  { code: 'BTC', label: 'Bitcoin', networks: ['Bitcoin'] },
  { code: 'ETH', label: 'Ethereum', networks: ['ERC20'] },
  { code: 'USDT', label: 'Tether', networks: ['ERC20', 'TRC20', 'BEP20'] },
  { code: 'USDC', label: 'USD Coin', networks: ['ERC20', 'BEP20'] },
];

const WIRE_FALLBACK = {
  bank_name: 'Not configured yet',
  beneficiary: '',
  iban: '',
  swift: '',
  account_number: '',
  bank_address: '',
  reference_prefix: 'WCDFS-',
  instructions: 'Please contact support — the platform admin has not published bank details yet.',
};

// Build the {coin: {network: address}} lookup expected by the deposit forms
// from the dynamic wallet list returned by /api/payment-methods.
const buildCryptoLookup = (wallets) => {
  const lookup = {};
  const byCoin = {};
  for (const w of (wallets || [])) {
    const coin = (w.coin || '').toUpperCase();
    const net = w.network || '';
    if (!coin || !net || !w.address) continue;
    lookup[coin] = lookup[coin] || {};
    lookup[coin][net] = w.address;
    byCoin[coin] = byCoin[coin] || { code: coin, label: coin, networks: [], memos: {}, instructions: {}, mins: {} };
    if (!byCoin[coin].networks.includes(net)) byCoin[coin].networks.push(net);
    byCoin[coin].memos[net] = w.memo || '';
    byCoin[coin].instructions[net] = w.instructions || '';
    byCoin[coin].mins[net] = w.min_deposit || 0;
  }
  const coins = Object.values(byCoin);
  return { lookup, coins };
};

const StatusPill = ({ status }) => {
  const map = {
    PENDING: 'bg-yellow-100 text-yellow-800',
    PROCESSING: 'bg-blue-100 text-blue-800',
    APPROVED: 'bg-emerald-100 text-emerald-800',
    COMPLETED: 'bg-emerald-100 text-emerald-800',
    REJECTED: 'bg-rose-100 text-rose-800',
    CANCELLED: 'bg-slate-200 text-slate-700',
  };
  return <span className={`px-2 py-1 rounded text-xs font-semibold ${map[status] || 'bg-slate-100 text-slate-700'}`}>{status}</span>;
};

const MethodCard = ({ icon: Icon, title, desc, testid, onClick }) => (
  <button
    onClick={onClick}
    data-testid={testid}
    className="group text-left p-6 bg-white border border-slate-200 rounded-2xl hover:border-brand-accent hover:shadow-md transition"
  >
    <div className="flex items-center justify-between mb-3">
      <div className="w-12 h-12 rounded-xl bg-brand-accent/10 flex items-center justify-center">
        <Icon size={26} weight="duotone" className="text-brand-accent" />
      </div>
      <ArrowRight size={18} className="text-slate-400 group-hover:text-brand-accent group-hover:translate-x-0.5 transition" />
    </div>
    <div className="font-bold text-slate-900">{title}</div>
    <div className="text-sm text-slate-500 mt-1">{desc}</div>
  </button>
);

const SandboxBanner = () => (
  <div className="mb-4 px-3 py-2 rounded-lg bg-yellow-50 border border-yellow-200 text-yellow-800 text-xs font-medium">
    Sandbox mode — no real funds are moved. Requests are submitted for admin review.
  </div>
);

// ---------- DEPOSIT FORMS ----------
// Credit-card deposits are handled off-platform — the client receives
// payment instructions via email from Operations. We deliberately do NOT
// collect PAN/CVV in the UI (PCI scope avoidance + no sandbox capture
// exposed to end users).
const CardDepositForm = ({ onClose }) => (
  <div className="space-y-4" data-testid="deposit-card-info">
    <div className="rounded-xl border border-slate-200 bg-slate-50 p-5">
      <div className="text-base font-semibold text-slate-900 mb-1" data-testid="deposit-card-info-title">
        Check your email for the payment details.
      </div>
      <div className="text-sm text-slate-600" data-testid="deposit-card-info-body">
        We&apos;ll send you the instructions needed to complete your card deposit.
      </div>
    </div>
    <div className="flex justify-end pt-2">
      <Button type="button" onClick={onClose} data-testid="deposit-card-info-done">Done</Button>
    </div>
  </div>
);

const WireDepositForm = ({ onClose, onDone, wire }) => {
  const [amount, setAmount] = useState('');
  const [currency, setCurrency] = useState(wire?.currency || 'USD');
  const [reference, setReference] = useState((wire?.reference || 'WCDFS-') + Math.random().toString(36).slice(2,8).toUpperCase());
  const [proofFile, setProofFile] = useState(null);
  const [proofInfo, setProofInfo] = useState(null); // { storage_path, filename, content_type, size }
  const [uploading, setUploading] = useState(false);
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);

  const handleProofChange = async (e) => {
    const f = e.target.files?.[0];
    setProofFile(null);
    setProofInfo(null);
    if (!f) return;
    if (f.size > 10 * 1024 * 1024) {
      toast.error('File exceeds 10 MB limit');
      e.target.value = '';
      return;
    }
    setProofFile(f);
    setUploading(true);
    try {
      const r = await uploadTransactionProof(f);
      setProofInfo(r.data);
      toast.success('Proof uploaded');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Proof upload failed');
      setProofFile(null);
      e.target.value = '';
    } finally { setUploading(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const metadata = { reference, notes, bank_id: wire?.id };
      if (proofInfo) {
        metadata.proof_storage_path = proofInfo.storage_path;
        metadata.proof_filename = proofInfo.filename;
        metadata.proof_content_type = proofInfo.content_type;
        metadata.proof_size = proofInfo.size;
      }
      await submitDeposit({
        amount: parseFloat(amount),
        method: 'WIRE',
        currency,
        metadata,
      });
      toast.success('Wire deposit submitted — funds will be credited after verification');
      onDone();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Deposit failed');
    } finally { setBusy(false); }
  };

  return (
    <form onSubmit={submit} className="space-y-4" data-testid="deposit-wire-form">
      <SandboxBanner />
      <div className="p-4 rounded-lg bg-slate-50 border border-slate-200 text-sm space-y-1">
        <div className="font-semibold text-slate-800 mb-2">Bank transfer instructions</div>
        <div><span className="text-slate-500">Bank:</span> {wire?.bank_name || '—'}</div>
        {wire?.beneficiary && <div><span className="text-slate-500">Beneficiary:</span> {wire.beneficiary}</div>}
        {wire?.iban && <div><span className="text-slate-500">IBAN:</span> <span className="tabular-nums font-mono">{wire.iban}</span></div>}
        {wire?.account_number && <div><span className="text-slate-500">Account number:</span> <span className="tabular-nums font-mono">{wire.account_number}</span></div>}
        {wire?.swift && <div><span className="text-slate-500">SWIFT/BIC:</span> {wire.swift}</div>}
        {wire?.bank_address && <div><span className="text-slate-500">Bank address:</span> {wire.bank_address}</div>}
        {wire?.beneficiary_address && <div><span className="text-slate-500">Beneficiary address:</span> {wire.beneficiary_address}</div>}
        <div><span className="text-slate-500">Reference:</span> <span className="font-mono">{reference}</span></div>
        {wire?.instructions && <div className="pt-2 text-slate-600 whitespace-pre-line">{wire.instructions}</div>}
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label>Amount</Label>
          <Input required type="number" min="10" step="0.01" value={amount} onChange={e=>setAmount(e.target.value)} data-testid="deposit-wire-amount" />
        </div>
        <div>
          <Label>Currency</Label>
          <Select value={currency} onValueChange={setCurrency}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              {['USD','EUR','GBP','CHF'].map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>
      <div>
        <Label>Proof of payment (optional)</Label>
        <input
          type="file"
          accept="image/*,.pdf"
          onChange={handleProofChange}
          disabled={uploading}
          className="w-full mt-1 border border-slate-300 rounded-md p-2 text-sm"
          data-testid="deposit-wire-proof-input"
        />
        <p className="text-xs text-slate-500 mt-1">
          {uploading ? 'Uploading…' :
            proofInfo ? `Attached: ${proofInfo.filename} (${(proofInfo.size/1024).toFixed(0)} KB)` :
            'PDF, JPG, PNG, WEBP up to 10 MB. The reference above is matched by our team.'}
        </p>
      </div>
      <div>
        <Label>Notes</Label>
        <Textarea rows={2} value={notes} onChange={e=>setNotes(e.target.value)} />
      </div>
      <div className="flex gap-2 pt-2">
        <Button type="button" variant="outline" onClick={onClose} className="flex-1">Cancel</Button>
        <Button type="submit" disabled={busy || uploading} className="flex-1" data-testid="deposit-wire-submit">{busy ? 'Submitting…' : 'Submit'}</Button>
      </div>
    </form>
  );
};

const CryptoDepositForm = ({ onClose, onDone, wallets }) => {
  const { lookup: CRYPTO_DEPOSIT_ADDRESSES, coins } = React.useMemo(() => buildCryptoLookup(wallets), [wallets]);
  const CRYPTOS = coins.length > 0 ? coins : CRYPTOS_FALLBACK;
  const [crypto, setCrypto] = useState(CRYPTOS[0]?.code || 'USDT');
  const cryptoObj = CRYPTOS.find(c => c.code === crypto) || CRYPTOS[0] || CRYPTOS_FALLBACK[0];
  const [network, setNetwork] = useState(cryptoObj.networks[0]);
  const [amount, setAmount] = useState('');
  const [txHash, setTxHash] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const cur = CRYPTOS.find(c => c.code === crypto);
    if (cur) setNetwork(cur.networks[0]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [crypto]);

  const address = CRYPTO_DEPOSIT_ADDRESSES[crypto]?.[network] || '—';
  const memo = cryptoObj?.memos?.[network] || '';
  const walletInstructions = cryptoObj?.instructions?.[network] || '';

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await submitDeposit({
        amount: parseFloat(amount),
        method: 'CRYPTO',
        currency: crypto,
        metadata: { network, deposit_address: address, tx_hash: txHash },
      });
      toast.success('Crypto deposit submitted — awaiting confirmations & admin approval');
      onDone();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Deposit failed');
    } finally { setBusy(false); }
  };

  return (
    <form onSubmit={submit} className="space-y-4" data-testid="deposit-crypto-form">
      <SandboxBanner />
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label>Cryptocurrency</Label>
          <Select value={crypto} onValueChange={setCrypto}>
            <SelectTrigger data-testid="deposit-crypto-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {CRYPTOS.map(c => <SelectItem key={c.code} value={c.code}>{c.code} — {c.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Network</Label>
          <Select value={network} onValueChange={setNetwork}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              {cryptoObj.networks.map(n => <SelectItem key={n} value={n}>{n}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="p-3 rounded-lg bg-slate-50 border border-slate-200 text-sm">
        <div className="text-slate-500 text-xs mb-1">Send {crypto} on the {network} network to:</div>
        <div className="font-mono text-xs break-all text-slate-800">{address}</div>
        {memo && <div className="text-xs mt-2"><span className="text-slate-500">Memo / Tag:</span> <span className="font-mono">{memo}</span></div>}
        {walletInstructions && <div className="text-xs mt-2 text-slate-600 whitespace-pre-line">{walletInstructions}</div>}
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label>Amount ({crypto})</Label>
          <Input required type="number" step="0.00000001" min="0" value={amount} onChange={e=>setAmount(e.target.value)} data-testid="deposit-crypto-amount" />
        </div>
        <div>
          <Label>Transaction hash</Label>
          <Input placeholder="0x..." value={txHash} onChange={e=>setTxHash(e.target.value)} />
        </div>
      </div>
      <div className="flex gap-2 pt-2">
        <Button type="button" variant="outline" onClick={onClose} className="flex-1">Cancel</Button>
        <Button type="submit" disabled={busy} className="flex-1" data-testid="deposit-crypto-submit">{busy ? 'Submitting…' : 'Submit'}</Button>
      </div>
    </form>
  );
};

// ---------- WITHDRAWAL FORMS ----------
const CardWithdrawForm = ({ balance, onClose, onDone }) => {
  const [amount, setAmount] = useState('');
  const [last4, setLast4] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await submitWithdrawal({
        amount: parseFloat(amount),
        method: 'CARD',
        currency: 'USD',
        metadata: { card_last4: last4 },
      });
      toast.success('Withdrawal to card submitted for approval');
      onDone();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Withdrawal failed');
    } finally { setBusy(false); }
  };

  return (
    <form onSubmit={submit} className="space-y-4" data-testid="withdraw-card-form">
      <SandboxBanner />
      <div>
        <Label>Amount (USD)</Label>
        <Input required type="number" min="10" max={balance} step="0.01" value={amount} onChange={e=>setAmount(e.target.value)} data-testid="withdraw-card-amount" />
        <p className="text-xs text-slate-500 mt-1">Available: ${balance?.toFixed(2)}</p>
      </div>
      <div>
        <Label>Card last 4 digits</Label>
        <Input required inputMode="numeric" maxLength={4} value={last4} onChange={e=>setLast4(e.target.value)} />
      </div>
      <div className="flex gap-2 pt-2">
        <Button type="button" variant="outline" onClick={onClose} className="flex-1">Cancel</Button>
        <Button type="submit" disabled={busy} className="flex-1" data-testid="withdraw-card-submit">{busy ? 'Submitting…' : 'Continue'}</Button>
      </div>
    </form>
  );
};

const WireWithdrawForm = ({ balance, accountCurrency, onClose, onDone }) => {
  const [amount, setAmount] = useState('');
  const [currency, setCurrency] = useState('USD');
  const [beneficiary, setBeneficiary] = useState('');
  const [bankName, setBankName] = useState('');
  const [iban, setIban] = useState('');
  const [swift, setSwift] = useState('');
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await submitWithdrawal({
        amount: parseFloat(amount),
        method: 'WIRE',
        currency,
        metadata: { beneficiary, bank_name: bankName, iban, swift, notes },
      });
      toast.success('Wire withdrawal submitted');
      onDone();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Withdrawal failed');
    } finally { setBusy(false); }
  };

  return (
    <form onSubmit={submit} className="space-y-4" data-testid="withdraw-wire-form">
      <SandboxBanner />
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label>Amount</Label>
          <Input required type="number" min="10" max={balance} step="0.01" value={amount} onChange={e=>setAmount(e.target.value)} data-testid="withdraw-wire-amount" />
          <p className="text-xs text-slate-500 mt-1">Available: {formatMoney(balance, accountCurrency)}</p>
        </div>
        <div>
          <Label>Currency</Label>
          <Select value={currency} onValueChange={setCurrency}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              {['USD','EUR','GBP','CHF'].map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>
      <div>
        <Label>Beneficiary name</Label>
        <Input required value={beneficiary} onChange={e=>setBeneficiary(e.target.value)} />
      </div>
      <div>
        <Label>Bank name</Label>
        <Input required value={bankName} onChange={e=>setBankName(e.target.value)} />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label>IBAN / Account</Label>
          <Input required value={iban} onChange={e=>setIban(e.target.value)} />
        </div>
        <div>
          <Label>SWIFT / BIC</Label>
          <Input value={swift} onChange={e=>setSwift(e.target.value)} />
        </div>
      </div>
      <div>
        <Label>Notes</Label>
        <Textarea rows={2} value={notes} onChange={e=>setNotes(e.target.value)} />
      </div>
      <div className="flex gap-2 pt-2">
        <Button type="button" variant="outline" onClick={onClose} className="flex-1">Cancel</Button>
        <Button type="submit" disabled={busy} className="flex-1" data-testid="withdraw-wire-submit">{busy ? 'Submitting…' : 'Submit'}</Button>
      </div>
    </form>
  );
};

const CryptoWithdrawForm = ({ balance, onClose, onDone, wallets }) => {
  const { coins } = React.useMemo(() => buildCryptoLookup(wallets), [wallets]);
  const CRYPTOS = coins.length > 0 ? coins : CRYPTOS_FALLBACK;
  const [crypto, setCrypto] = useState(CRYPTOS[0]?.code || 'USDT');
  const cryptoObj = CRYPTOS.find(c => c.code === crypto) || CRYPTOS[0] || CRYPTOS_FALLBACK[0];
  const [network, setNetwork] = useState(cryptoObj.networks[0]);
  const [amount, setAmount] = useState('');
  const [address, setAddress] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const cur = CRYPTOS.find(c => c.code === crypto);
    if (cur) setNetwork(cur.networks[0]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [crypto]);

  const validateAddress = (addr, net) => {
    if (!addr) return false;
    if (net === 'Bitcoin') return /^(bc1|[13])[a-zA-HJ-NP-Z0-9]{20,90}$/.test(addr);
    if (net === 'TRC20') return /^T[a-zA-HJ-NP-Z0-9]{33}$/.test(addr);
    return /^0x[a-fA-F0-9]{40}$/.test(addr);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!validateAddress(address, network)) {
      toast.error('Invalid wallet address for the selected network');
      return;
    }
    setBusy(true);
    try {
      await submitWithdrawal({
        amount: parseFloat(amount),
        method: 'CRYPTO',
        currency: crypto,
        metadata: { network, wallet_address: address },
      });
      toast.success('Crypto withdrawal submitted for approval');
      onDone();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Withdrawal failed');
    } finally { setBusy(false); }
  };

  return (
    <form onSubmit={submit} className="space-y-4" data-testid="withdraw-crypto-form">
      <SandboxBanner />
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label>Cryptocurrency</Label>
          <Select value={crypto} onValueChange={setCrypto}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              {CRYPTOS.map(c => <SelectItem key={c.code} value={c.code}>{c.code} — {c.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Network</Label>
          <Select value={network} onValueChange={setNetwork}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              {cryptoObj.networks.map(n => <SelectItem key={n} value={n}>{n}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>
      <div>
        <Label>Wallet address</Label>
        <Input required value={address} onChange={e=>setAddress(e.target.value)} data-testid="withdraw-crypto-address" />
        <p className="text-xs text-slate-500 mt-1">Double-check the address — crypto withdrawals cannot be reversed.</p>
      </div>
      <div>
        <Label>Amount ({crypto} equivalent)</Label>
        <Input required type="number" step="0.00000001" min="0" value={amount} onChange={e=>setAmount(e.target.value)} data-testid="withdraw-crypto-amount" />
      </div>
      <div className="flex gap-2 pt-2">
        <Button type="button" variant="outline" onClick={onClose} className="flex-1">Cancel</Button>
        <Button type="submit" disabled={busy} className="flex-1" data-testid="withdraw-crypto-submit">{busy ? 'Submitting…' : 'Submit'}</Button>
      </div>
    </form>
  );
};

// ---------- MAIN PAGE ----------
const Transactions = () => {
  const [account, setAccount] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [depositOpen, setDepositOpen] = useState(false);
  const [depositMethod, setDepositMethod] = useState(null);
  const [withdrawOpen, setWithdrawOpen] = useState(false);
  const [withdrawMethod, setWithdrawMethod] = useState(null);
  const [paymentMethods, setPaymentMethods] = useState({ banks: [], crypto_wallets: [] });

  const fetchData = async () => {
    try {
      const [accountsRes, transactionsRes, pmRes] = await Promise.all([
        getAccounts(), getTransactions(),
        axios.get(`${API}/payment-methods`).catch(() => ({ data: { banks: [], crypto_wallets: [] } })),
      ]);
      if (accountsRes.data.length > 0) setAccount(accountsRes.data[0]);
      setTransactions(transactionsRes.data);
      setPaymentMethods(pmRes.data || { banks: [], crypto_wallets: [] });
    } catch { toast.error('Failed to load data'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, []);

  const closeAndRefresh = () => {
    setDepositOpen(false); setDepositMethod(null);
    setWithdrawOpen(false); setWithdrawMethod(null);
    fetchData();
  };

  if (loading) return <div className="p-8"><div className="loader"></div></div>;

  const balance = account?.balance || 0;
  const equity = account?.equity === undefined ? 0 : account.equity;
  const freeMargin = account?.free_margin === undefined ? 0 : account.free_margin;

  return (
    <div className="p-8" data-testid="client-transactions-page">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-brand-primary">Deposits & Withdrawals</h1>
        <p className="text-slate-600 mt-1">Manage your account funds</p>
      </div>

      <div className="grid md:grid-cols-3 gap-6 mb-6">
        <div className="bg-white p-6 rounded-xl border border-slate-200">
          <div className="text-sm text-slate-600 mb-2">Available Balance</div>
          <div className="text-3xl font-bold text-brand-primary tabular-nums" data-testid="client-balance">{formatMoney(balance, account?.currency)}</div>
        </div>
        <div className="bg-white p-6 rounded-xl border border-slate-200">
          <div className="text-sm text-slate-600 mb-2">Free Margin</div>
          <div className="text-3xl font-bold text-brand-primary tabular-nums">{formatMetric(freeMargin, account?.currency)}</div>
        </div>
        <div className="bg-white p-6 rounded-xl border border-slate-200">
          <div className="text-sm text-slate-600 mb-2">Equity</div>
          <div className="text-3xl font-bold text-brand-primary tabular-nums">{formatMetric(equity, account?.currency)}</div>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-4 mb-8">
        <button
          onClick={() => { setDepositMethod(null); setDepositOpen(true); }}
          data-testid="open-deposit-btn"
          className="group relative overflow-hidden text-left p-8 rounded-2xl bg-gradient-to-br from-emerald-500 to-emerald-700 text-white hover:shadow-xl transition"
        >
          <div className="flex items-center justify-between">
            <div>
              <div className="text-xs uppercase tracking-widest opacity-80 mb-2">Fund your account</div>
              <div className="text-3xl font-black">Deposit</div>
              <div className="text-sm opacity-90 mt-2">Card · Wire · Crypto</div>
            </div>
            <ArrowDownLeft size={44} weight="duotone" />
          </div>
        </button>
        <button
          onClick={() => { setWithdrawMethod(null); setWithdrawOpen(true); }}
          data-testid="open-withdraw-btn"
          className="group relative overflow-hidden text-left p-8 rounded-2xl bg-gradient-to-br from-slate-800 to-slate-900 text-white hover:shadow-xl transition"
        >
          <div className="flex items-center justify-between">
            <div>
              <div className="text-xs uppercase tracking-widest opacity-80 mb-2">Take profits</div>
              <div className="text-3xl font-black">Withdraw</div>
              <div className="text-sm opacity-90 mt-2">Card · Wire · Crypto</div>
            </div>
            <ArrowUpRight size={44} weight="duotone" />
          </div>
        </button>
      </div>

      {/* DEPOSIT DIALOG */}
      <Dialog open={depositOpen} onOpenChange={(v) => { setDepositOpen(v); if (!v) setDepositMethod(null); }}>
        <DialogContent className="max-w-2xl" data-testid="deposit-dialog">
          <DialogHeader>
            <DialogTitle>{depositMethod ? `Deposit via ${depositMethod === 'CARD' ? 'Credit Card' : depositMethod === 'WIRE' ? 'Wire Transfer' : 'Crypto'}` : 'Choose deposit method'}</DialogTitle>
            <DialogDescription>{depositMethod ? 'Enter details below to submit your deposit.' : 'Select how you would like to fund your account.'}</DialogDescription>
          </DialogHeader>
          {!depositMethod && (
            <div className="grid sm:grid-cols-3 gap-3">
              <MethodCard icon={CreditCard} title="Credit Card" desc="Get payment instructions by email" testid="deposit-method-card" onClick={() => setDepositMethod('CARD')} />
              <MethodCard icon={Bank} title="Wire Transfer" desc="Bank-to-bank with reference" testid="deposit-method-wire" onClick={() => setDepositMethod('WIRE')} />
              <MethodCard icon={CurrencyBtc} title="Crypto" desc="BTC · ETH · USDT · USDC" testid="deposit-method-crypto" onClick={() => setDepositMethod('CRYPTO')} />
            </div>
          )}
          {depositMethod === 'CARD' && <CardDepositForm onClose={() => setDepositMethod(null)} />}
          {depositMethod === 'WIRE' && <WireDepositForm onClose={() => setDepositMethod(null)} onDone={closeAndRefresh} wire={paymentMethods.banks[0] || WIRE_FALLBACK} />}
          {depositMethod === 'CRYPTO' && <CryptoDepositForm onClose={() => setDepositMethod(null)} onDone={closeAndRefresh} wallets={paymentMethods.crypto_wallets} />}
        </DialogContent>
      </Dialog>

      {/* WITHDRAWAL DIALOG */}
      <Dialog open={withdrawOpen} onOpenChange={(v) => { setWithdrawOpen(v); if (!v) setWithdrawMethod(null); }}>
        <DialogContent className="max-w-2xl" data-testid="withdraw-dialog">
          <DialogHeader>
            <DialogTitle>{withdrawMethod ? `Withdraw via ${withdrawMethod === 'CARD' ? 'Credit Card' : withdrawMethod === 'WIRE' ? 'Wire Transfer' : 'Crypto'}` : 'Choose withdrawal method'}</DialogTitle>
            <DialogDescription>{withdrawMethod ? 'Enter details below to submit your withdrawal.' : 'Select how you would like to receive your funds.'}</DialogDescription>
          </DialogHeader>
          {!withdrawMethod && (
            <div className="grid sm:grid-cols-3 gap-3">
              <MethodCard icon={CreditCard} title="Credit Card" desc="Refund to original card" testid="withdraw-method-card" onClick={() => setWithdrawMethod('CARD')} />
              <MethodCard icon={Bank} title="Wire Transfer" desc="To your bank account" testid="withdraw-method-wire" onClick={() => setWithdrawMethod('WIRE')} />
              <MethodCard icon={CurrencyBtc} title="Crypto" desc="To your wallet" testid="withdraw-method-crypto" onClick={() => setWithdrawMethod('CRYPTO')} />
            </div>
          )}
          {withdrawMethod === 'CARD' && <CardWithdrawForm balance={balance} onClose={() => setWithdrawMethod(null)} onDone={closeAndRefresh} />}
          {withdrawMethod === 'WIRE' && <WireWithdrawForm balance={balance} accountCurrency={account?.currency} onClose={() => setWithdrawMethod(null)} onDone={closeAndRefresh} />}
          {withdrawMethod === 'CRYPTO' && <CryptoWithdrawForm balance={balance} onClose={() => setWithdrawMethod(null)} onDone={closeAndRefresh} wallets={paymentMethods.crypto_wallets} />}
        </DialogContent>
      </Dialog>

      {/* HISTORY */}
      <div className="bg-white rounded-xl border border-slate-200">
        <div className="p-6 border-b border-slate-200">
          <h2 className="text-xl font-bold text-brand-primary">Transaction History</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="client-transactions-table">
            <thead className="bg-slate-50">
              <tr className="text-left uppercase text-xs tracking-wider text-slate-600">
                <th className="p-4">Transaction ID</th>
                <th className="p-4">Type</th>
                <th className="p-4">Method</th>
                <th className="p-4 text-right">Amount</th>
                <th className="p-4">Status</th>
                <th className="p-4">Notes</th>
                <th className="p-4">Date</th>
              </tr>
            </thead>
            <tbody>
              {transactions.length === 0 ? <tr><td colSpan="7" className="p-8 text-center text-slate-500">No transactions yet</td></tr>
                : transactions.map(t => {
                  const isAdjust = t.payment_method === 'ADMIN_ADJUSTMENT';
                  const displayType = isAdjust ? (t.transaction_type === 'DEPOSIT' ? 'CREDIT' : 'DEBIT') : t.transaction_type;
                  const methodLabel = isAdjust ? 'Admin Adjustment' : (t.payment_method || '—');
                  const shortId = 'TXN-' + String(t.id || '').replace(/-/g, '').slice(0, 8).toUpperCase();
                  return (
                  <tr key={t.id} className="border-t border-slate-100" data-testid={`tx-row-${t.id}`}>
                    <td className="p-4 text-xs font-mono text-slate-700" title={t.id}>{shortId}</td>
                    <td className="p-4">
                      <span className={`px-2 py-1 rounded text-xs font-semibold ${t.transaction_type === 'DEPOSIT' ? 'bg-emerald-100 text-emerald-800' : 'bg-orange-100 text-orange-800'}`}>
                        {displayType}
                      </span>
                    </td>
                    <td className="p-4 text-xs text-slate-600">{methodLabel}</td>
                    <td className="p-4 text-right font-semibold tabular-nums">{formatMoney(t.amount, t.currency)}</td>
                    <td className="p-4"><StatusPill status={t.status} /></td>
                    <td className="p-4 text-xs text-slate-600 max-w-xs truncate">{t.admin_notes || (t.metadata?.reference || t.metadata?.notes || t.metadata?.reason) || '—'}</td>
                    <td className="p-4 text-xs text-slate-500">{new Date(t.created_at).toLocaleString()}</td>
                  </tr>
                  );
                })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default Transactions;
